import { AdvizeoStatusMessageComponent } from '@/components/advizeo-status-message/advizeo-status-message';
import { ObservableInputsManager } from '@/shared/utils/angular-sdk';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiCheckboxComponent } from '@/ui/checkbox/ui-checkbox';
import { UiFieldComponent } from '@/ui/field/ui-field';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { type UiStandaloneTab, UiStandaloneTabsComponent } from '@/ui/standalone-tabs/ui-standalone-tabs';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    type OnChanges,
    type OnInit,
    Output,
    signal,
    type SimpleChanges
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, type UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, firstValueFrom, type Observable } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { AdvizeoInputDirective } from '../../../directives/advizeo-input/advizeo-input.directive';
import { DashboardMode, DashboardScopeType } from '../../../interfaces/Dashboard.interface';
import {
    type EmptyTailoredWidget,
    type TailoredWidget,
    type TailoredWidgetConfig,
    type TailoredWidgetId,
    TailoredWidgetScopeType,
    type TailoredWidgetTemplateId,
    type TailoredWidgetToSave,
    TailoredWidgetType
} from '../../../interfaces/TailoredWidget.interface';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { TailoredWidgetsProvider } from '../../../providers/tailored-widgets/tailored-widgets.provider';
import { TailoredWidgetSpawnerComponent } from '../../tailored-widget-spawner/tailored-widget-spawner';
import {
    type TailoredWidgetConfigChangedEvent,
    TailoredWidgetConfigEditorComponent
} from '../tailored-widget-config-editor/tailored-widget-config-editor';

export interface TailoredWidgetEditorIntent {
    editionIntent?: {
        widgetId: TailoredWidgetId;
    };
    duplicationIntent?: TailoredWidgetEditorDuplicationIntent;
    creationIntent?: TailoredWidgetEditorCreationIntent;
    requiredDashboardModes: DashboardMode[];
    requiredDashboardScopeType: DashboardScopeType;
}

interface TailoredWidgetEditorCreationIntent {
    widgetType: TailoredWidgetType;
}

interface TailoredWidgetEditorDuplicationIntent {
    widgetId: TailoredWidgetId;
}

export interface WidgetTemplateEditorCreationIntent extends TailoredWidgetEditorCreationIntent {
    widgetType: TailoredWidgetType.TEMPLATE_WIDGET;
    widgetTemplateId: TailoredWidgetTemplateId;
}

const DATA_POINTS_PERFORMANCE_WARNING_THRESHOLD = 20000; // Arbitrary threshold for performance warning
const DATA_POINTS_LIMIT_THRESHOLD = 50000; // This limit is based on the maximum number of data points that can be handled by cubejs

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./tailored-widget-editor.scss'],
    selector: 'tailored-widget-editor',
    templateUrl: 'tailored-widget-editor.html',
    imports: [
        UiStandaloneTabsComponent,
        NgClass,
        TailoredWidgetConfigEditorComponent,
        TailoredWidgetSpawnerComponent,
        AdvizeoStatusMessageComponent,
        UiSectionComponent,
        UiSectionContentComponent,
        FormsModule,
        ReactiveFormsModule,
        UiFieldComponent,
        AdvizeoInputDirective,
        UiCheckboxComponent,
        UiButtonComponent,
        AsyncPipe,
        TranslatePipe
    ]
})
export class TailoredWidgetEditorComponent implements OnInit, OnChanges {
    private _tailoredWidgetsProvider = inject(TailoredWidgetsProvider);
    private _formBuilder = inject(UntypedFormBuilder);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _translateService = inject(TranslateService);
    private _cdRef = inject(ChangeDetectorRef);

    private _observableInputsManager = new ObservableInputsManager<TailoredWidgetEditorComponent>(this);
    @Input() actionIntent!: TailoredWidgetEditorIntent;
    actionIntent$ = this._observableInputsManager.observe('actionIntent');

    @Output() actionResult: EventEmitter<TailoredWidgetId> = new EventEmitter<TailoredWidgetId>();

    selectedTabId: number = 0;

    widgetBaseConfigForm!: UntypedFormGroup;
    widget$!: Observable<TailoredWidget | EmptyTailoredWidget>;

    configToEdit!: TailoredWidgetConfig;

    private _config$ = new BehaviorSubject<TailoredWidgetConfig | null>(null);
    debouncedConfig$ = this._config$.pipe(debounceTime(500));

    isConfigValid!: boolean;
    private _forbiddenDashboardModes: DashboardMode[] = [];

    context$!: Observable<Record<string, any>>;

    DashboardMode = DashboardMode;
    dataPointsWarningLevel = signal<'none' | 'performance' | 'limit'>('none');

    originalAllowedDashboardModes!: Record<DashboardMode, boolean>;

    canBeReferencedInCatalog$!: Observable<boolean>;
    tabsOptions$!: Observable<UiStandaloneTab[]>;

    ngOnInit() {
        this.context$ = this._dashboardingProvider.getGenericDashboardingContext$();

        this.widget$ = this.actionIntent$.pipe(
            filter(isNotNullish),
            switchMap(actionIntent => {
                if (isNotNullish(actionIntent.editionIntent)) {
                    return this._tailoredWidgetsProvider.getWidget$(actionIntent.editionIntent.widgetId);
                }
                if (isNotNullish(actionIntent.duplicationIntent)) {
                    return this._tailoredWidgetsProvider.getDuplicatedWidget$(actionIntent.duplicationIntent.widgetId);
                }
                if (isNotNullish(actionIntent.creationIntent)) {
                    if (actionIntent.creationIntent.widgetType === TailoredWidgetType.TEMPLATE_WIDGET) {
                        return this._tailoredWidgetsProvider.getEmptyWidget$(
                            (actionIntent.creationIntent as WidgetTemplateEditorCreationIntent).widgetTemplateId
                        );
                    } else {
                        return this._tailoredWidgetsProvider.getEmptyWidget$();
                    }
                }
                throw new Error('unknown intent for widget editor:: ' + actionIntent);
            })
        );

        firstValueFrom(this.widget$.pipe(map(widget => widget.configuration))).then(config => {
            this.configToEdit = config;
        });

        this.widgetBaseConfigForm = this._formBuilder.group({
            name: [null, Validators.required],
            isInCatalog: [false],
            canBeMonoSite: [true],
            canBeMultiSite: [true]
        });

        this.widget$.pipe(untilDestroyed(this)).subscribe(widget => {
            this.originalAllowedDashboardModes = {
                [DashboardMode.MONO]: widget.canBeMonoSite,
                [DashboardMode.MULTI]: widget.canBeMultiSite
            };
            this.widgetBaseConfigForm.patchValue({
                name: widget.name,
                isInCatalog: widget.isInCatalog,
                canBeMonoSite: widget.canBeMonoSite,
                canBeMultiSite: widget.canBeMultiSite
            });
        });

        this.canBeReferencedInCatalog$ = this.actionIntent$.pipe(
            filter(isNotNullish),
            switchMap(actionIntent => this._tailoredWidgetsProvider.canBeReferencedInCatalog$(actionIntent.requiredDashboardScopeType)),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.tabsOptions$ = this.canBeReferencedInCatalog$.pipe(
            map(canBeReferencedInCatalog => {
                if (canBeReferencedInCatalog) {
                    return [
                        {
                            id: 0,
                            label: this._translateService.instant('TAILORED_WIDGET.TAB_TITLE.DISPLAY_CONFIGURATION')
                        },
                        {
                            id: 1,
                            label: this._translateService.instant('TAILORED_WIDGET.TAB_TITLE.REFERENCING_CONFIGURATION')
                        }
                    ];
                } else {
                    return [
                        {
                            id: 0,
                            label: this._translateService.instant('TAILORED_WIDGET.TAB_TITLE.DISPLAY_CONFIGURATION')
                        }
                    ];
                }
            })
        );
    }

    selectedTabIdChange(currentTabId: number) {
        this.selectedTabId = currentTabId;
    }

    ngOnChanges(changes: SimpleChanges) {
        this._observableInputsManager.triggerChanges(changes);
    }

    onDataPointCountChange(count: number) {
        if (count > DATA_POINTS_LIMIT_THRESHOLD) {
            this.dataPointsWarningLevel.set('limit');
        } else if (count > DATA_POINTS_PERFORMANCE_WARNING_THRESHOLD) {
            this.dataPointsWarningLevel.set('performance');
        } else {
            this.dataPointsWarningLevel.set('none');
        }
    }

    specificConfigChanged(config: TailoredWidgetConfigChangedEvent) {
        this.isConfigValid = config.isValid;
        this._forbiddenDashboardModes = config.forbiddenDashboardModes;
        this.dataPointsWarningLevel.set('none');
        this.uncheckForbiddenDashboardModes(config.forbiddenDashboardModes);
        this._cdRef.detectChanges();
        if (this.isConfigValid) {
            this._config$.next(config.config);
            this.requestDefaultName();
        }
    }

    saveWidget() {
        if (this.isConfigValid && this.widgetBaseConfigForm.valid) {
            this._tailoredWidgetsProvider.saveWidget(this.getWidgetToSave()).then(widgetId => {
                if (widgetId) this.actionResult.emit(widgetId);
                else this.actionResult.emit();
            });
        }
    }

    private requestDefaultName() {
        const previewedWidgetConfig = this._config$.getValue();

        if (this.widgetBaseConfigForm.controls.name.pristine) {
            if (
                previewedWidgetConfig?.widgetType === TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET &&
                previewedWidgetConfig.businessIntelligenceConfiguration.shell
            ) {
                this.widgetBaseConfigForm.patchValue({
                    name: previewedWidgetConfig.businessIntelligenceConfiguration.shell.shellConfig.title
                });
            }
            if (
                previewedWidgetConfig?.widgetType === TailoredWidgetType.TEMPLATE_WIDGET &&
                isNotNullish(this.actionIntent.creationIntent)
            ) {
                const widgetTemplateId = (this.actionIntent.creationIntent as WidgetTemplateEditorCreationIntent).widgetTemplateId;
                this.widgetBaseConfigForm.patchValue({
                    name: this._translateService.instant('TAILORED_WIDGET.TEMPLATE.DEFAULT_NAME', { widgetTemplateId })
                });
            }
        }
        this._cdRef.detectChanges();
    }

    private getWidgetToSave(): TailoredWidgetToSave {
        const formValue = this.widgetBaseConfigForm.value;
        const previewedWidgetConfig = this._config$.getValue();
        const widgetType: TailoredWidgetScopeType =
            this.actionIntent.requiredDashboardScopeType === DashboardScopeType.USER
                ? TailoredWidgetScopeType.USER
                : TailoredWidgetScopeType.PROJECT;

        const widgetToSave: TailoredWidgetToSave = {
            widgetId: this.actionIntent.editionIntent ? this.actionIntent.editionIntent.widgetId : null,
            name: formValue.name,
            type: widgetType,
            isInCatalog: formValue.isInCatalog,
            canBeMonoSite: formValue.isInCatalog ? formValue.canBeMonoSite : true,
            canBeMultiSite: formValue.isInCatalog ? formValue.canBeMultiSite : true
        };

        switch (previewedWidgetConfig?.widgetType) {
            case TailoredWidgetType.TEMPLATE_WIDGET:
                widgetToSave.widgetTemplateId = previewedWidgetConfig?.tailoredWidgetTemplateConfiguration?.widgetTemplateId;
                widgetToSave.widgetTemplateParams =
                    previewedWidgetConfig?.tailoredWidgetTemplateConfiguration?.templateWidgetConfigurationParams ?? undefined;
                break;
            case TailoredWidgetType.BUSINESS_INTELLIGENCE_WIDGET:
                widgetToSave.configuration = previewedWidgetConfig?.businessIntelligenceConfiguration;
        }

        return widgetToSave;
    }

    isDashboardModeDisabled(dashboardMode: DashboardMode) {
        return (
            (this.actionIntent && this.actionIntent.creationIntent && this.actionIntent.requiredDashboardModes.includes(dashboardMode)) ||
            this._forbiddenDashboardModes.includes(dashboardMode) ||
            (isNotNullish(this.actionIntent.editionIntent) &&
                this.originalAllowedDashboardModes &&
                this.originalAllowedDashboardModes[dashboardMode]) ||
            (isNotNullish(this.actionIntent.duplicationIntent) &&
                this.originalAllowedDashboardModes &&
                this.originalAllowedDashboardModes[dashboardMode])
        );
    }

    private uncheckForbiddenDashboardModes(dashboardModes: DashboardMode[]) {
        for (const mode of dashboardModes) {
            switch (mode) {
                case DashboardMode.MONO:
                    this.widgetBaseConfigForm.controls.canBeMonoSite.setValue(false);
                    break;
                case DashboardMode.MULTI:
                    this.widgetBaseConfigForm.controls.canBeMultiSite.setValue(false);
                    break;
            }
        }
    }
}
